import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@heroui/react'
import { Bell, CheckCheck } from 'lucide-react'
import apiClient from '../api/client'
import { toast } from 'sonner'

export interface BellNotification {
  id: string
  org_id: string
  /** NOTE: the REST serializer names this `type_field`, not `type`. */
  type_field: string
  channel: string
  payload: { title?: string; body?: string }
  sent_at: string
  read_at: string | null
}

function formatWhen(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString()
}

/**
 * Header notification bell (N6): unread badge + recent dropdown.
 * Portal dropdown mirrors ActionMenu (HeroUI Dropdown stays open in v3).
 * Polls unread-count every 15s (same cadence as the Live wall).
 */
export default function NotificationsBell() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState({ top: 0, right: 0 })

  const { data: unreadData } = useQuery<{ unread: number }>({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => (await apiClient.get('/notifications/unread-count')).data,
    refetchInterval: 15000,
  })
  const unread = unreadData?.unread ?? 0

  const { data: recent = [], isLoading: recentLoading } = useQuery<BellNotification[]>({
    queryKey: ['notifications', 'recent'],
    queryFn: async () => {
      const raw = (await apiClient.get('/notifications?per_page=8')).data
      return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
    },
    enabled: open,
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
  }

  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiClient.put(`/notifications/${id}/read`),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['notifications', 'recent'] })
      await queryClient.cancelQueries({ queryKey: ['notifications', 'unread'] })
      const prevRecent = queryClient.getQueryData<BellNotification[]>(['notifications', 'recent'])
      const prevUnread = queryClient.getQueryData<{ unread: number }>(['notifications', 'unread'])
      queryClient.setQueryData<BellNotification[]>(['notifications', 'recent'], (old) =>
        old ? old.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)) : old,
      )
      queryClient.setQueryData<{ unread: number }>(['notifications', 'unread'], (old) =>
        old ? { unread: Math.max(0, old.unread - 1) } : old,
      )
      return { prevRecent, prevUnread }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prevRecent) queryClient.setQueryData(['notifications', 'recent'], ctx.prevRecent)
      if (ctx?.prevUnread) queryClient.setQueryData(['notifications', 'unread'], ctx.prevUnread)
      toast.error('Failed to mark notification as read')
    },
    onSettled: invalidateAll,
  })

  const markAllMutation = useMutation({
    mutationFn: () => apiClient.post('/notifications/read-all'),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications', 'recent'] })
      await queryClient.cancelQueries({ queryKey: ['notifications', 'unread'] })
      const prevRecent = queryClient.getQueryData<BellNotification[]>(['notifications', 'recent'])
      const prevUnread = queryClient.getQueryData<{ unread: number }>(['notifications', 'unread'])
      const now = new Date().toISOString()
      queryClient.setQueryData<BellNotification[]>(['notifications', 'recent'], (old) =>
        old ? old.map((n) => (n.read_at ? n : { ...n, read_at: now })) : old,
      )
      queryClient.setQueryData<{ unread: number }>(['notifications', 'unread'], { unread: 0 })
      return { prevRecent, prevUnread }
    },
    onSuccess: () => toast.success('All notifications marked as read'),
    onError: (_e, _v, ctx) => {
      if (ctx?.prevRecent) queryClient.setQueryData(['notifications', 'recent'], ctx.prevRecent)
      if (ctx?.prevUnread) queryClient.setQueryData(['notifications', 'unread'], ctx.prevUnread)
      toast.error('Failed to mark all as read')
    },
    onSettled: invalidateAll,
  })

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (!containerRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open ])

  const toggle = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setCoords({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) })
    }
    setOpen((v) => !v)
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <div className="relative">
        <Button
          ref={buttonRef as unknown as React.RefObject<HTMLButtonElement>}
          isIconOnly
          variant="ghost"
          className="rounded-full text-default-500 hover:text-foreground"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          aria-haspopup="true"
          aria-expanded={open}
          onPress={toggle}
        >
          <Bell size={20} />
        </Button>
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-danger text-foreground text-[11px] font-bold flex items-center justify-center pointer-events-none"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </div>

      {open && createPortal(
        <div
          ref={panelRef}
          role="menu"
          aria-label="Recent notifications"
          className="fixed w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl bg-surface border border-border shadow-2xl z-[9999] overflow-hidden"
          style={{ top: coords.top, right: coords.right }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <p className="font-semibold text-foreground">
              Notifications{unread > 0 ? ` (${unread} unread)` : ''}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted hover:text-foreground"
              isDisabled={unread === 0 || markAllMutation.isPending}
              onPress={() => markAllMutation.mutate()}
            >
              <CheckCheck size={16} className="mr-1" /> Mark all read
            </Button>
          </div>

          <div className="max-h-[320px] overflow-y-auto">
            {recentLoading ? (
              <p className="px-4 py-8 text-center text-sm text-muted">Loading…</p>
            ) : recent.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted">You are all caught up.</p>
            ) : (
              <ul className="flex flex-col">
                {recent.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { if (!n.read_at) markReadMutation.mutate(n.id) }}
                      className="w-full text-left px-4 py-3 flex gap-3 hover:bg-surface-secondary transition-colors border-b border-border/50 last:border-0"
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-1.5 size-2 shrink-0 rounded-full ${n.read_at ? 'bg-transparent' : 'bg-spotify-green'}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {n.payload?.title || n.type_field}
                        </span>
                        {n.payload?.body && (
                          <span className="block truncate text-xs text-muted">{n.payload.body}</span>
                        )}
                        <span className="block text-[11px] text-muted mt-0.5">{formatWhen(n.sent_at)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-border">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/notifications') }}
              className="w-full px-4 py-2.5 text-sm font-medium text-center text-spotify-green hover:bg-surface-secondary transition-colors"
            >
              View all notifications
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
